import { apiFetch } from '@/lib/api';
import type { I18nKey } from '@/lib/i18n';

export enum UploadKind {
  PHOTO = 'photo',
  SIGNATURE = 'signature',
  PDF = 'pdf',
}

interface SignResp {
  url: string;
  key: string;
}

interface SignGetResp {
  url: string;
}

const S3_PREFIX = 's3:';
const DATA_URL_PREFIX = 'data:';

/**
 * Ceiling for the offline data: URL fallback — 512 KB of file, which base64
 * inflates to roughly 700 KB of document body (client #48).
 *
 * Sized so the things that genuinely cannot be re-captured still work offline:
 * signatures (~10–40 KB PNG) and compressed field photos (~100–300 KB JPEG,
 * see compressImage) fit comfortably.
 *
 * This ceiling does NOT keep contract PDFs out — real scans land around
 * 260–350 KB, well under it. PDFs are excluded by kind in signAndPut instead;
 * do not rely on size alone here.
 */
export const DATA_URL_FALLBACK_MAX_BYTES = 512 * 1024;

/**
 * Thrown when an upload failed and inlining the file was not permitted.
 * Carries an i18n key so the caller can surface a real message via t()
 * instead of the user losing the file silently (client #48).
 */
export class UploadFailedError extends Error {
  readonly i18nKey: I18nKey;
  constructor(i18nKey: I18nKey, detail: string) {
    super(`upload failed (${i18nKey}): ${detail}`);
    this.name = 'UploadFailedError';
    this.i18nKey = i18nKey;
  }
}

// navigator.onLine is a weak signal for "online", but a FALSE value is
// trustworthy: browsers only report offline when there is no network
// interface at all. That asymmetry is exactly what we need — we widen the
// fallback only when we are sure a retry could not have succeeded.
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Upload a file to S3 via a presigned PUT and return a stored reference of the
// form "s3:<key>". Both "s3:<key>" and data: URLs are accepted by
// resolveUploadUrl on read.
//
// Client #48: this used to swallow ANY presign/PUT failure and fall back to an
// embedded data: URL. One transient failure therefore baked a multi-megabyte
// base64 blob into the document permanently — the client's service-contract
// PDF ended up as a data: URL their browser could not open, and no retry ever
// replaced it because the record looked complete.
//
// The fallback now needs BOTH conditions to hold:
//   1. offline — a retry could not have worked anyway, and the field data
//      (signature, photo) is unrepeatable, so inlining beats losing it;
//   2. under DATA_URL_FALLBACK_MAX_BYTES — small enough to live in a document
//      body without breaking sync, PDF export or the browser.
// Anything else rejects with UploadFailedError so the caller can tell the user
// the upload failed instead of persisting an unusable document.
export async function signAndPut(
  file: File,
  kind: UploadKind
): Promise<string> {
  try {
    const { url, key } = await apiFetch<SignResp>('/uploads/sign', {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
        kind,
      }),
    });
    const put = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!put.ok) throw new Error(`s3 put failed: ${put.status}`);
    return `${S3_PREFIX}${key}`;
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    const offline = isOffline();
    // PDFs never inline, whatever their size. The size ceiling alone was not
    // enough: real service contracts scan to ~260-350 KB, comfortably under it,
    // so they still landed in the customer doc as base64 and replicated to every
    // device (client #48). Signatures and photos keep the fallback because field
    // capture cannot be repeated; a PDF is a file on disk the office can simply
    // re-upload once back online.
    if (
      offline &&
      kind !== UploadKind.PDF &&
      file.size <= DATA_URL_FALLBACK_MAX_BYTES
    ) {
      return readAsDataUrl(file);
    }
    throw new UploadFailedError(
      offline ? 'upload.failedOfflineTooLarge' : 'upload.failed',
      detail
    );
  }
}

// Downscale + re-encode an image before upload. Field devices produce 5–20
// photos per visit (client spec) at multi-MB each; capping the longest edge
// and re-encoding as JPEG keeps storage and offline-sync small. Falls back to
// the original file if the browser can't decode it (e.g. HEIC without support).
export async function compressImage(
  file: File,
  maxDim = 1600,
  quality = 0.7
): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.size < 400_000) {
      bitmap.close?.();
      return file; // already small enough — skip re-encode
    }
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>(res =>
      canvas.toBlob(res, 'image/jpeg', quality)
    );
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// Compress (if an image) then upload. Convenience wrapper for photo capture.
export async function signAndPutImage(file: File): Promise<string> {
  const compressed = await compressImage(file);
  return signAndPut(compressed, UploadKind.PHOTO);
}

// Resolve a stored reference to a URL usable in href / window.open.
// "s3:<key>" references are exchanged for a short-lived presigned GET URL.
//
// Data URLs pass through unchanged and MUST keep doing so (client #48):
// tightening signAndPut does not rewrite history, so records written before
// the fix still hold inline data: URLs and have to stay openable.
export async function resolveUploadUrl(stored: string): Promise<string> {
  if (!stored) return '';
  if (stored.startsWith(DATA_URL_PREFIX)) return stored;
  if (!stored.startsWith(S3_PREFIX)) return stored;
  const key = stored.slice(S3_PREFIX.length);
  const { url } = await apiFetch<SignGetResp>('/uploads/sign-get', {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
  return url;
}
